import { computeBilling, formatMoney } from '@cartiq/shared';
import type { BillingConfig, CartItem } from '@cartiq/shared';

interface Props {
  cart: CartItem[];
  billing?: BillingConfig | null;
  /** Chosen delivery destination (when the store has per-location fees). */
  deliveryLocationSlug?: string;
}

/**
 * Itemised order summary — subtotal, then each delivery/tax/fee charge, then the
 * billed total. Derived from the same `computeBilling` the API charges with, so
 * the shopper sees exactly what they'll pay.
 */
export default function BillingSummary({ cart, billing, deliveryLocationSlug }: Props) {
  if (cart.length === 0) return null;
  const b = computeBilling(cart, billing ?? null, { deliveryLocationSlug });
  const fmt = (n: number) => formatMoney(n, b.currency);

  return (
    <div className="flex flex-col gap-1.5 text-sm">
      <div className="flex items-center justify-between text-mid">
        <span>Subtotal</span>
        <span>{fmt(b.subtotal)}</span>
      </div>
      {b.lines.map((line) => (
        <div key={line.key} className="flex items-center justify-between text-mid">
          <span>{line.label}</span>
          <span>{line.amount > 0 ? fmt(line.amount) : 'Free'}</span>
        </div>
      ))}
      <div className="mt-1 flex items-center justify-between border-t border-border pt-2 text-base font-bold text-primary">
        <span>Total</span>
        <span>{fmt(b.total)}</span>
      </div>
      {b.belowMinimum && (
        <p className="pt-1 text-xs font-medium text-error">
          Minimum order: {fmt(b.belowMinimum.minimum)}
        </p>
      )}
      {b.issues?.map((issue) => (
        <p key={issue.code} className="pt-1 text-xs font-medium text-error">
          {issue.message}
        </p>
      ))}
      {billing?.delivery?.notes && (
        <p className="pt-1 text-xs text-mid">{billing.delivery.notes}</p>
      )}
      {billing?.paymentNotes && (
        <p className="pt-1 text-xs text-mid">{billing.paymentNotes}</p>
      )}
    </div>
  );
}
